import { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  Tune,
} from '@mui/icons-material'
import NewModificationForm from './NewModificationForm'
import ModificationRow from './ModificationRow'
import { api } from '../../services/api'

export default function ModificationsPanel({ planId, modifications, onModificationsChange }) {
  const [expanded, setExpanded] = useState(true)
  const [loading, setLoading] = useState(false)

  const mods = modifications || []

  useEffect(() => {
    if (!planId || !onModificationsChange) return
    let cancelled = false
    setLoading(true)
    api.get(`/modifications?planId=${planId}`)
      .then(data => {
        if (cancelled) return
        if (Array.isArray(data) && data.length > 0) {
          onModificationsChange(data)
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [planId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreated = useCallback(async () => {
    if (!planId) return
    try {
      const data = await api.get(`/modifications?planId=${planId}`)
      if (Array.isArray(data) && onModificationsChange) {
        onModificationsChange(data)
      }
    } catch {}
  }, [planId, onModificationsChange])

  const handleToggleIgnore = useCallback((modId) => {
    const updated = mods.map(m => m.id === modId ? { ...m, isIgnored: !m.isIgnored } : m)
    if (onModificationsChange) onModificationsChange(updated)

    api.post(`/modifications/${modId}/ignore`, { planId }).catch(err => {
      console.error('Failed to toggle ignore:', err)
    })
  }, [mods, planId, onModificationsChange])

  const handleRemove = useCallback((modId) => {
    const updated = mods.filter(m => m.id !== modId)
    if (onModificationsChange) onModificationsChange(updated)

    api.delete(`/modifications/${modId}?planId=${planId}`).catch(err => {
      console.error('Failed to remove modification:', err)
    })
  }, [mods, planId, onModificationsChange])

  const handleReorder = useCallback((dragIndex, dropIndex) => {
    const reordered = [...mods]
    const [moved] = reordered.splice(dragIndex, 1)
    reordered.splice(dropIndex, 0, moved)
    const withPositions = reordered.map((m, i) => ({ ...m, listPosition: i + 1 }))
    if (onModificationsChange) onModificationsChange(withPositions)

    api.post('/modifications/reorder', {
      planId,
      ordering: withPositions.map((m, i) => ({ id: m.id, listPosition: i + 1 })),
    }).catch(err => {
      console.error('Failed to reorder:', err)
    })
  }, [mods, planId, onModificationsChange])

  const activeCount = mods.filter((m) => !m.isIgnored).length
  const totalCount = mods.length

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      backgroundColor: 'background.paper',
      overflow: 'hidden',
    }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Tune sx={{ fontSize: 18, color: 'secondary.main' }} />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Modifications
        </Typography>
        {!expanded && totalCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {activeCount} active modification{activeCount !== 1 ? 's' : ''}
            {totalCount !== activeCount && ` (${totalCount - activeCount} ignored)`}
          </Typography>
        )}
        {loading && <CircularProgress size={14} />}
        <Tooltip title={expanded ? 'Collapse Modifications Panel' : 'Expand Modifications Panel'}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}>
            {expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 2, py: 1.5 }}>
          <NewModificationForm planId={planId} onCreated={handleCreated} />
        </Box>

        {totalCount > 0 && (
          <>
            <Divider />
            <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
              {mods.map((mod, index) => (
                <ModificationRow
                  key={mod.id}
                  mod={mod}
                  onToggleIgnore={handleToggleIgnore}
                  onRemove={handleRemove}
                  dragHandleProps={{
                    onDragStart: () => {},
                    onDragOver: (e) => e.preventDefault(),
                    onDrop: () => handleReorder(index, 0),
                    draggable: true,
                  }}
                />
              ))}
            </Box>
          </>
        )}

        {totalCount === 0 && !loading && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No modifications yet — use the form above to create one.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  )
}
